import React, { useCallback, useEffect, useState } from 'react';
import { Container } from './ui/Container';
import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type ModalType = null | 'privacy' | 'terms';
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} data-id="element-1">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-2" />

      {/* Modal */}
      <motion.div role="dialog" aria-label={type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'} className="relative w-full max-w-2xl max-h-[80vh] bg-slate-900 border border-indigo-500/20 rounded-xl shadow-2xl shadow-black/40 flex flex-col" initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} transition={{
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-3">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800 shrink-0" data-id="element-4">
          <h2 className="text-xl font-bold text-white" data-id="element-5">
            {type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800" aria-label="Cerrar" data-id="element-6">
            <XIcon className="w-5 h-5" data-id="element-7" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto p-6 text-slate-300 text-sm leading-relaxed space-y-5" data-id="element-8">
          {type === 'privacy' ? <PrivacyContent data-id="element-9" /> : <TermsContent data-id="element-10" />}
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-slate-400 text-xs" data-id="element-11">
        Última actualización: febrero 2026
      </p>

      <div data-id="element-12">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-13">
          1. Información que recopilamos
        </h3>
        <p data-id="element-14">
          Al adquirir RESET 3AM, recopilamos únicamente la información necesaria
          para procesar tu compra: dirección de correo electrónico y datos de
          pago. Los datos de pago son gestionados íntegramente por nuestro
          procesador de pagos externo y nunca se almacenan en nuestros
          servidores.
        </p>
      </div>

      <div data-id="element-15">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-16">
          2. Uso de la información
        </h3>
        <p data-id="element-17">
          Tu correo electrónico se utiliza exclusivamente para enviarte el
          enlace de descarga del producto y, en caso necesario, comunicaciones
          relacionadas con tu compra. No enviamos correos promocionales ni
          compartimos tu información con terceros con fines de marketing.
        </p>
      </div>

      <div data-id="element-18">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-19">
          3. Cookies y tecnologías de seguimiento
        </h3>
        <p data-id="element-20">
          Este sitio utiliza cookies esenciales para el funcionamiento básico de
          la página y cookies analíticas anónimas para comprender cómo los
          visitantes interactúan con el sitio. No utilizamos cookies de
          publicidad ni de seguimiento de terceros.
        </p>
      </div>

      <div data-id="element-21">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-22">
          4. Servicios de terceros
        </h3>
        <p data-id="element-23">
          Utilizamos procesadores de pago externos para gestionar las
          transacciones de forma segura. Estos proveedores tienen sus propias
          políticas de privacidad y operan bajo estándares de seguridad PCI-DSS.
        </p>
      </div>

      <div data-id="element-24">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-25">5. Tus derechos</h3>
        <p data-id="element-26">
          Tienes derecho a acceder, rectificar o eliminar tus datos personales
          en cualquier momento. También puedes solicitar una copia de los datos
          que tenemos sobre ti. Para ejercer cualquiera de estos derechos, ponte
          en contacto con nosotros a través de los canales indicados en tu
          correo de confirmación de compra.
        </p>
      </div>

      <div data-id="element-27">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-28">
          6. Retención de datos
        </h3>
        <p data-id="element-29">
          Conservamos tu dirección de correo electrónico mientras sea necesario
          para proporcionarte acceso al producto adquirido. Puedes solicitar la
          eliminación de tus datos en cualquier momento.
        </p>
      </div>

      <div data-id="element-30">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-31">7. Seguridad</h3>
        <p data-id="element-32">
          Implementamos medidas de seguridad técnicas y organizativas razonables
          para proteger tu información personal contra accesos no autorizados,
          pérdida o alteración.
        </p>
      </div>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-slate-400 text-xs" data-id="element-33">
        Última actualización: febrero 2026
      </p>

      <div data-id="element-34">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-35">
          1. Licencia de uso
        </h3>
        <p data-id="element-36">
          Al adquirir RESET 3AM, se te otorga una licencia personal,
          intransferible y no exclusiva para usar el contenido digital incluido
          en el kit. Esta licencia es únicamente para tu uso personal y no
          permite la redistribución, reventa ni compartición del material con
          terceros.
        </p>
      </div>

      <div data-id="element-37">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-38">
          2. Propiedad intelectual
        </h3>
        <p data-id="element-39">
          Todo el contenido de RESET 3AM — incluyendo textos, diseños,
          plantillas, ilustraciones y la estructura del programa — está
          protegido por derechos de autor. Queda prohibida la reproducción total
          o parcial del material sin autorización expresa por escrito.
        </p>
      </div>

      <div data-id="element-40">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-41">
          3. Naturaleza del producto
        </h3>
        <p data-id="element-42">
          RESET 3AM es un producto digital educativo e informativo. No
          constituye asesoramiento médico, psicológico ni profesional de ningún
          tipo. Si experimentas problemas de sueño crónicos, te recomendamos
          consultar con un profesional de la salud.
        </p>
      </div>

      <div data-id="element-43">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-44">
          4. Política de reembolso
        </h3>
        <p data-id="element-45">
          Debido a la naturaleza digital del producto y su entrega inmediata, no
          se ofrecen reembolsos una vez completada la descarga. Al realizar la
          compra, aceptas que recibes acceso instantáneo al contenido y
          renuncias al derecho de desistimiento aplicable a productos digitales.
        </p>
      </div>

      <div data-id="element-46">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-47">
          5. Limitación de responsabilidad
        </h3>
        <p data-id="element-48">
          RESET 3AM se proporciona "tal cual". No garantizamos resultados
          específicos derivados del uso del material. En ningún caso seremos
          responsables de daños directos, indirectos, incidentales o
          consecuentes que puedan surgir del uso o la imposibilidad de uso del
          producto.
        </p>
      </div>

      <div data-id="element-49">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-50">6. Modificaciones</h3>
        <p data-id="element-51">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones entrarán en vigor desde su publicación en
          esta página. El uso continuado del producto tras cualquier cambio
          constituye la aceptación de los nuevos términos.
        </p>
      </div>

      <div data-id="element-52">
        <h3 className="text-slate-200 font-semibold mb-2" data-id="element-53">7. Ley aplicable</h3>
        <p data-id="element-54">
          Estos términos se rigen por la legislación vigente en la jurisdicción
          del vendedor. Cualquier disputa será resuelta en los tribunales
          competentes de dicha jurisdicción.
        </p>
      </div>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-slate-950 py-12 border-t border-slate-900" data-id="element-55">
        <Container data-id="element-56">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm" data-id="element-57">
            <p data-id="element-58">
              © {new Date().getFullYear()} RESET 3AM. Todos los derechos
              reservados.
            </p>
            <div className="flex gap-6" data-id="element-59">
              <button onClick={() => setActiveModal('terms')} className="hover:text-slate-300 transition-colors" data-id="element-60">
                Términos
              </button>
              <button onClick={() => setActiveModal('privacy')} className="hover:text-slate-300 transition-colors" data-id="element-61">
                Privacidad
              </button>
            </div>
          </div>
        </Container>
      </footer>

      <AnimatePresence data-id="element-62">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-63" />}
      </AnimatePresence>
    </>;
}